import { create } from 'zustand'
import type { Case } from '../types'
import { getCases, getCase, updateCaseStatus } from '../lib/api'

interface CaseFilters {
  status?: string
  severity?: number
  platform?: string
  search?: string
}

interface CaseState {
  cases: Case[]
  selectedCase: Case | null
  filters: CaseFilters
  isLoading: boolean
  error: string | null
  setFilters: (filters: Partial<CaseFilters>) => void
  fetchCases: () => Promise<void>
  selectCase: (id: string) => Promise<void>
  updateCaseStatus: (id: string, status: string) => Promise<void>
  clearSelection: () => void
}

export const useCaseStore = create<CaseState>()((set, get) => ({
  cases: [],
  selectedCase: null,
  filters: {},
  isLoading: false,
  error: null,

  setFilters: (filters) => {
    set((state) => ({ filters: { ...state.filters, ...filters } }))
  },

  fetchCases: async () => {
    set({ isLoading: true, error: null })
    try {
      const { status, severity } = get().filters
      const response = await getCases({
        ...(status && { status }),
        ...(severity && { severity }),
        page_size: 100,
      })
      set({ cases: response.items, isLoading: false })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch cases'
      set({ error: message, isLoading: false })
    }
  },

  selectCase: async (id: string) => {
    try {
      const caseData = await getCase(id)
      set({ selectedCase: caseData })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch case'
      set({ error: message })
    }
  },

  updateCaseStatus: async (id: string, status: string) => {
    try {
      const updated = await updateCaseStatus(id, status)
      set((state) => ({
        cases: state.cases.map((c) => (c.id === id ? updated : c)),
        selectedCase: state.selectedCase?.id === id ? updated : state.selectedCase,
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update case'
      set({ error: message })
    }
  },

  clearSelection: () => set({ selectedCase: null }),
}))
